import { css } from 'lit';

/** Legacy `hidePanel`'s fade, which the match results used. */
export const ANNOUNCEMENT_FADE_OUT_MS = 750;

/** Legacy `hidePanel`'s long fade (its `longfadenoclick` argument), which only
 *  the level-up used. */
export const ANNOUNCEMENT_LONG_FADE_OUT_MS = 1500;

/**
 * Shared shell for the three ports of legacy's one `#custom-msg` panel: the BTR
 * and CTF end-of-match results and the level-up notice. Each is a
 * full-viewport, click-through overlay with a `.result` that pops in on
 * `showPanel`'s 0.75s curve and fades back down on `hidePanel`'s, at the length
 * its caller passes to announcementFadeTiming. That timing ends the fade as the
 * dismiss timer fires, with no hiding state to get out of step. Each keeps its
 * own look and size; `.result` is centred, and the level-up moves its `top`.
 *
 * Usage: `static override styles = [tokens, announcementStyles, css\`...\`];`
 */
export const announcementStyles = css`
  :host {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 100;
    /* Click-through, so selection, callout and tap flash can't happen; only
       the typography is inherited otherwise. */
    font-family: var(--font-family);
  }

  /* Transform centring (not negative margins), so it stays centred at any
     size. The keyframes keep the translate in every frame, or the scale
     would drop the centring while they run. */
  .result {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    animation:
      resultIn 0.75s cubic-bezier(0.23, 1, 0.32, 1),
      resultOut var(--hide-duration, 0s) cubic-bezier(0.23, 1, 0.32, 1) var(--hide-delay, 0s) forwards;
  }

  @keyframes resultIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  @keyframes resultOut {
    to {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
  }

  /* Legacy's gentle pulse, on the BTR trophy and the level-up badge. */
  @keyframes bountyshake {
    0% {
      transform: scale(0.95);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(0.95);
    }
  }
`;

/** The `.result` element's fade timing, for styleMap: a `fadeMs` fade that ends
 *  exactly as a dismiss timer of `durationMs` fires. One shorter than the fade
 *  just fades for its whole life. */
export function announcementFadeTiming(
  durationMs: number,
  fadeMs: number,
): { '--hide-duration': string; '--hide-delay': string } {
  const hideMs = Math.min(fadeMs, durationMs);
  return { '--hide-duration': `${hideMs}ms`, '--hide-delay': `${durationMs - hideMs}ms` };
}
